import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  WorkflowFailureReason,
  WorkflowInput,
  WorkflowStatus,
} from "@sales-ops-amplify/shared";
import {
  activateWorkflow,
  aiDraftWorkflow,
  archiveWorkflow,
  cloneWorkflow,
  createWorkflow,
  deactivateWorkflow,
  enrollInWorkflow,
  getWorkflow,
  listWorkflowEnrollments,
  listWorkflowTemplates,
  listWorkflows,
  retryWorkflowEnrollments,
  updateWorkflow,
} from "@/api/workflows";

export function useWorkflows(params?: {
  status?: WorkflowStatus | "all";
  q?: string;
}) {
  return useQuery({
    queryKey: ["workflows", params],
    queryFn: () => listWorkflows(params),
  });
}

export function useWorkflow(workflowId: string | undefined) {
  return useQuery({
    queryKey: ["workflow", workflowId],
    queryFn: () => getWorkflow(workflowId!),
    enabled: Boolean(workflowId),
  });
}

export function useWorkflowTemplates() {
  return useQuery({
    queryKey: ["workflow-templates"],
    queryFn: listWorkflowTemplates,
  });
}

export function useWorkflowEnrollments(workflowId: string | undefined) {
  return useQuery({
    queryKey: ["workflow-enrollments", workflowId],
    queryFn: () => listWorkflowEnrollments(workflowId!),
    enabled: Boolean(workflowId),
  });
}

export function useCreateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: WorkflowInput & { templateId?: string; aiPrompt?: string },
    ) => createWorkflow(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useUpdateWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      workflowId: string;
      patch: Partial<WorkflowInput> & { status?: WorkflowStatus };
    }) => updateWorkflow(args.workflowId, args.patch),
    onSuccess: (_r, args) => {
      void qc.invalidateQueries({ queryKey: ["workflows"] });
      void qc.invalidateQueries({ queryKey: ["workflow", args.workflowId] });
    },
  });
}

export function useWorkflowStatusAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      workflowId: string;
      action: "activate" | "deactivate" | "archive" | "clone";
    }) => {
      if (args.action === "activate") return activateWorkflow(args.workflowId);
      if (args.action === "deactivate")
        return deactivateWorkflow(args.workflowId);
      if (args.action === "archive") return archiveWorkflow(args.workflowId);
      return cloneWorkflow(args.workflowId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["workflows"] });
      void qc.invalidateQueries({ queryKey: ["workflow"] });
    },
  });
}

export function useAiDraftWorkflow() {
  return useMutation({
    mutationFn: (prompt: string) => aiDraftWorkflow(prompt),
  });
}

export function useEnrollWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      workflowId: string;
      leadIds?: string[];
      accountIds?: string[];
      apolloPersonIds?: string[];
    }) =>
      enrollInWorkflow(args.workflowId, {
        leadIds: args.leadIds,
        accountIds: args.accountIds,
        apolloPersonIds: args.apolloPersonIds,
      }),
    onSuccess: (_r, args) => {
      void qc.invalidateQueries({
        queryKey: ["workflow-enrollments", args.workflowId],
      });
      void qc.invalidateQueries({ queryKey: ["workflow", args.workflowId] });
      void qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}

export function useRetryWorkflowEnrollments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      workflowId: string;
      enrollmentIds?: string[];
      reason?: WorkflowFailureReason;
    }) =>
      retryWorkflowEnrollments(args.workflowId, {
        enrollmentIds: args.enrollmentIds,
        reason: args.reason,
      }),
    onSuccess: (_r, args) => {
      void qc.invalidateQueries({
        queryKey: ["workflow-enrollments", args.workflowId],
      });
      void qc.invalidateQueries({ queryKey: ["workflows"] });
    },
  });
}
